<template>
  <div class="card">
    <div class="card-header d-flex align-items-center justify-content-between">
      <h5 class="mb-0">{{ $t('profile.sections.certifications') }}</h5>
      <div class="d-flex align-items-center gap-2">
        <button 
          v-if="canCreateCertificates"
          class="btn btn-sm btn-primary" 
          @click="openAddCertificateModal"
        >
          <i class="ti ti-plus me-1"></i>
          {{ $t('profile.certifications.addCertificate') }}
        </button>
      </div>
    </div>
    <div class="card-body">
      <!-- Upload progress -->
      <div v-if="isUploading" class="progress mb-3">
        <div class="progress-bar progress-bar-striped progress-bar-animated" 
             role="progressbar" 
             :style="{ width: uploadProgress + '%' }" 
             :aria-valuenow="uploadProgress" 
             aria-valuemin="0" 
             aria-valuemax="100">
          {{ uploadProgress }}%
        </div>
      </div>
      
      <!-- Error message -->
      <div v-if="error" class="alert alert-danger mb-3" role="alert">
        {{ error }}
      </div>
    
      <!-- Certificates list -->
      <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
        <div v-for="cert in certificates" :key="cert.id" class="col">
          <div class="card border shadow h-100">
            <div class="card-body p-4 position-relative">
              <!-- Certificate Title -->
              <div class="d-flex justify-content-between align-items-start mb-3">
                <div class="flex-grow-1 me-2">
                  <h6 class="fw-semibold mb-1 text-truncate">{{ cert.name }}</h6>
                  <small class="text-muted">{{ cert.certificateTypeName }}</small>
                </div>
                <button 
                  v-if="canDeleteCertificates"
                  class="btn btn-sm btn-light border-0 p-1" 
                  @click="handleDelete(cert)"
                  :disabled="isLoading || deletingId === cert.id"
                  :title="$t('common.delete')"
                  style="width: 28px; height: 28px;"
                >
                  <i v-if="deletingId === cert.id" class="ti ti-loader ti-spin small"></i>
                  <i v-else class="ti ti-x small text-muted"></i>
                </button>
              </div>
              
              <!-- Certificate Details -->
              <div class="mb-3">
                <div class="d-flex align-items-center mb-2">
                  <i class="ti ti-calendar text-muted me-2" style="font-size: 14px;"></i>
                  <small class="text-muted">{{ formatDate(cert.issueDate) }} - {{ formatDate(cert.expirationDate) }}</small>
                </div>
                
                <!-- Status Badge -->
                <div>
                  <span v-if="cert.isExpired" class="badge bg-danger-subtle text-danger border border-danger-subtle">
                    {{ $t('profile.certifications.expired') }}
                  </span>
                  <span v-else-if="cert.daysUntilExpiration <= 30" class="badge bg-warning-subtle text-warning border border-warning-subtle">
                    {{ $t('profile.certifications.expiringSoon', { days: cert.daysUntilExpiration }) }}
                  </span>
                  <span v-else class="badge bg-success-subtle text-success border border-success-subtle">
                    {{ $t('profile.certifications.valid') }}
                  </span>
                </div>
              </div>
              
              <!-- View Button -->
              <div v-if="cert.fileUploadId" class="mt-auto">
                <button 
                  class="btn btn-primary btn-sm w-100" 
                  @click="handlePreviewFile(cert)"
                >
                  <i class="ti ti-eye me-1"></i>
                  {{ $t('common.view') }}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div v-if="isLoading && !certificates.length" class="col-12 text-center">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
        
     
      </div>
      <div v-if="!isLoading && !certificates.length" class="col-12">
          <div class="d-flex flex-column align-items-center justify-content-center text-center py-5">
            <i class="ti ti-certificate text-muted mb-3" style="font-size: 3rem;"></i>
            <p class="text-muted mb-0">{{ $t('profile.certifications.noCertificates') }}</p>
          </div>
        </div>
      <!-- Hidden file input -->
      <input 
        type="file" 
        ref="fileInput" 
        class="d-none" 
        accept="application/pdf" 
        @change="handleFileUpload"
        multiple
      />
      
      <!-- PDF Preview Modal - Updated to match RfqAttachmentsCard approach -->
      <div class="modal fade" id="pdfPreviewModal" tabindex="-1" aria-labelledby="pdfPreviewModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-xl">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="pdfPreviewModalLabel">{{ getPreviewFileName(previewingFile) }}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body" style="min-height: 70vh;">
              <div v-if="isLoadingPreview" class="text-center p-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">{{ $t('common.loading') }}</span>
                </div>
              </div>
              <div v-else-if="previewError" class="alert alert-danger">
                {{ previewError }}
              </div>
              <div v-else-if="previewFileData" class="preview-container">
                <vue-files-preview 
                  :file="previewFileData" 
                  :excelOptions="{
                    mode: 'edit',
                    showToolbar: true,
                    showGrid: true,
                    showContextmenu: false
                  }" 
                  @success="handlePreviewSuccess"
                  @error="handlePreviewError"
                />
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-sm btn-secondary" data-bs-dismiss="modal">Close</button>
              <a class="btn btn-sm btn-primary" @click="downloadCurrentPreview">
                <i class="ti ti-download me-1"></i> Download
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Add Certificate Modal -->
      <div class="modal fade" id="addCertificateModal" tabindex="-1" aria-labelledby="addCertificateModalLabel" aria-hidden="true">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="addCertificateModalLabel">{{ $t('profile.certifications.addCertificate') }}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <form @submit.prevent="submitCertificate">
                <!-- Certificate Selection -->
                <div class="mb-3">
                  <label class="form-label">
                    {{ $t('profile.certifications.certificateType') }}
                    <span class="text-danger">*</span>
                  </label>
                  <select 
                    class="form-select" 
                    v-model="certificateForm.certificateId"
                    :class="{ 'is-invalid': certificateFormErrors.certificateId }"
                    @change="onCertificateChange"
                    required
                  >
                    <option value="">{{ $t('common.pleaseSelect') }}</option>
                    <option 
                      v-for="cert in availableCertificates" 
                      :key="cert.id" 
                      :value="cert.id"
                    >
                      {{ cert.name }} {{ cert.code ? `(${cert.code})` : '' }}
                    </option>
                  </select>
                  <div v-if="certificateFormErrors.certificateId" class="invalid-feedback">
                    {{ certificateFormErrors.certificateId }}
                  </div>
                </div>

                <!-- Certificate Number (Optional) -->
                <div class="mb-3">
                  <label class="form-label">{{ $t('profile.certifications.certificateNumber') }}</label>
                  <input 
                    type="text" 
                    class="form-control"
                    v-model="certificateForm.certificateNumber"
                    :placeholder="$t('profile.certifications.certificateNumberPlaceholder')"
                  />
                </div>

                <!-- Issue Date -->
                <div class="mb-3">
                  <label class="form-label">
                    {{ $t('profile.certifications.issueDate') }}
                    <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <input 
                      type="text" 
                      class="form-control"
                      v-model="formattedIssueDate"
                      :class="{ 'is-invalid': certificateFormErrors.issueDate }"
                      :placeholder="dateFormat"
                      @input="onFormattedIssueDateChange"
                      required
                    />
                    <input 
                      type="date" 
                      class="form-control"
                      v-model="certificateForm.issueDate"
                      @change="onIssueDateChange"
                      style="max-width: 50px; opacity: 0; position: absolute; right: 10px; top: 50%; transform: translateY(-50%); z-index: 10;"
                      title="Click to open date picker"
                    />
                    <span class="input-group-text">
                      <i class="ti ti-calendar" style="cursor: pointer;" @click="$event.target.parentElement.previousElementSibling.focus()"></i>
                    </span>
                  </div>
                  <div v-if="certificateFormErrors.issueDate" class="invalid-feedback">
                    {{ certificateFormErrors.issueDate }}
                  </div>
                  <div class="form-text">{{ $t('profile.certifications.dateFormatHelp', { format: dateFormat }) }}</div>
                </div>

                <!-- Expire Date -->
                <div class="mb-3">
                  <label class="form-label">
                    {{ $t('profile.certifications.expireDate') }}
                    <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <input 
                      type="text" 
                      class="form-control"
                      v-model="formattedExpireDate"
                      :class="{ 'is-invalid': certificateFormErrors.expireDate }"
                      :placeholder="dateFormat"
                      @input="onFormattedExpireDateChange"
                      required
                    />
                    <input 
                      type="date" 
                      class="form-control"
                      v-model="certificateForm.expireDate"
                      style="max-width: 50px; opacity: 0; position: absolute; right: 10px; top: 50%; transform: translateY(-50%); z-index: 10;"
                      title="Click to open date picker"
                    />
                    <span class="input-group-text">
                      <i class="ti ti-calendar" style="cursor: pointer;" @click="$event.target.parentElement.previousElementSibling.focus()"></i>
                    </span>
                  </div>
                  <div v-if="certificateFormErrors.expireDate" class="invalid-feedback">
                    {{ certificateFormErrors.expireDate }}
                  </div>
                  <div class="form-text">
                    <span v-if="selectedCertificateInterval">
                      {{ $t('profile.certifications.autoCalculated', { interval: selectedCertificateInterval }) }}
                    </span>
                    <span v-else>
                      {{ $t('profile.certifications.dateFormatHelp', { format: dateFormat }) }}
                    </span>
                  </div>
                </div>

                <!-- Certificate File Upload (Required) -->
                <div class="mb-3">
                  <label class="form-label">
                    {{ $t('profile.certifications.certificateFile') }}
                    <span class="text-danger">*</span>
                  </label>
                  <input 
                    type="file" 
                    class="form-control"
                    ref="certificateFileInput"
                    accept="application/pdf,.pdf"
                    @change="onCertificateFileChange"
                    :class="{ 'is-invalid': certificateFormErrors.file }"
                    required
                  />
                  <div v-if="certificateFormErrors.file" class="invalid-feedback">
                    {{ certificateFormErrors.file }}
                  </div>
                  <div class="form-text">{{ $t('profile.certifications.fileHelp') }}</div>
                  
                  <!-- Upload Progress -->
                  <div v-if="isCertificateFileUploading" class="progress mt-2">
                    <div 
                      class="progress-bar progress-bar-striped progress-bar-animated" 
                      role="progressbar" 
                      :style="{ width: certificateFileUploadProgress + '%' }"
                    >
                      {{ certificateFileUploadProgress }}%
                    </div>
                  </div>
                  
                  <!-- Selected File Info -->
                  <div v-if="certificateForm.selectedFile" class="mt-2">
                    <div class="alert alert-info d-flex align-items-center">
                      <i class="ti ti-file me-2"></i>
                      <span>{{ $t('profile.certifications.fileSelected') }}: {{ certificateForm.uploadedFileName }}</span>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="cancelCertificateModal">
                {{ $t('common.cancel') }}
              </button>
              <button 
                type="button" 
                class="btn btn-primary" 
                @click="submitCertificate"
                :disabled="isSubmittingCertificate"
              >
                <span v-if="isSubmittingCertificate" class="spinner-border spinner-border-sm me-1"></span>
                {{ $t('common.save') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import fileManagementService from '@/services/fileManagementService';
import { VueFilesPreview } from 'vue-files-preview';
import Swal from 'sweetalert2';
import tenantInfoService from '@/services/tenantInfoService';
import certificateService from '@/services/certificateService';
import tenantCertificateService from '@/services/tenantCertificateService';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { usePermissions } from '@/composables/usePermissions';

export default {
  name: 'ProfileCertificationsCard',
  components: {
    VueFilesPreview
  },
  emits: ['certificates-updated'],
  setup(props, { emit }) {
    const store = useStore();
    const { formatDate } = useDateFormatter();
    const { hasPermission } = usePermissions();
    const certificates = ref([]);
    const isLoading = ref(false);
    const isUploading = ref(false);
    const uploadProgress = ref(0);
    const error = ref(null);
    const fileInput = ref(null);
    const totalSize = ref(0);
    const currentUser = computed(() => store.getters['auth/currentUser']);
    const deletingId = ref(null);
    const previewingFile = ref({});
    const previewFile = ref(null);
    const previewError = ref(null);
    const isLoadingPreview = ref(false);
    let pdfPreviewModal = null;
    let addCertificateModal = null;

    // Certificate form variables
    const availableCertificates = ref([]);
    const isSubmittingCertificate = ref(false);
    const selectedCertificateInterval = ref(null);
    const certificateForm = ref({
      certificateId: '',
      issueDate: new Date().toISOString().split('T')[0], // Today's date
      expireDate: '',
      certificateNumber: '',
      uploadedFileId: null,
      uploadedFileName: ''
    });
    const certificateFormErrors = ref({
      certificateId: '',
      issueDate: '',
      expireDate: '',
      file: ''
    });
    
    // File upload related variables
    const certificateFileInput = ref(null);
    const isCertificateFileUploading = ref(false);
    const certificateFileUploadProgress = ref(0);

    // Date format helpers
    const dateFormat = computed(() => store.getters['settings/dateFormat']);
    
    // Computed properties for formatted date inputs
    const formattedIssueDate = computed({
      get: () => {
        if (!certificateForm.value.issueDate) return '';
        return formatDate(certificateForm.value.issueDate);
      },
      set: (value) => {
        if (!value) {
          certificateForm.value.issueDate = '';
          return;
        }
        // Parse the formatted date back to YYYY-MM-DD format
        const parsedDate = parseFormattedDate(value);
        if (parsedDate) {
          certificateForm.value.issueDate = parsedDate;
        }
      }
    });

    const formattedExpireDate = computed({
      get: () => {
        if (!certificateForm.value.expireDate) return '';
        return formatDate(certificateForm.value.expireDate);
      },
      set: (value) => {
        if (!value) {
          certificateForm.value.expireDate = '';
          return;
        }
        // Parse the formatted date back to YYYY-MM-DD format
        const parsedDate = parseFormattedDate(value);
        if (parsedDate) {
          certificateForm.value.expireDate = parsedDate;
        }
      }
    });

    // Helper function to parse formatted date back to YYYY-MM-DD
    const parseFormattedDate = (formattedDate) => {
      if (!formattedDate) return null;
      
      try {
        // Handle different date formats
        const format = dateFormat.value;
        let day, month, year;
        
        if (format === 'DD/MM/YYYY') {
          const parts = formattedDate.split('/');
          if (parts.length === 3) {
            day = parts[0].padStart(2, '0');
            month = parts[1].padStart(2, '0');
            year = parts[2];
          }
        } else if (format === 'MM/DD/YYYY') {
          const parts = formattedDate.split('/');
          if (parts.length === 3) {
            month = parts[0].padStart(2, '0');
            day = parts[1].padStart(2, '0');
            year = parts[2];
          }
        } else if (format === 'YYYY-MM-DD') {
          const parts = formattedDate.split('-');
          if (parts.length === 3) {
            year = parts[0];
            month = parts[1].padStart(2, '0');
            day = parts[2].padStart(2, '0');
          }
        }
        
        if (day && month && year) {
          // Validate the date
          const date = new Date(year, month - 1, day);
          if (date.getFullYear() == year && date.getMonth() == month - 1 && date.getDate() == day) {
            return `${year}-${month}-${day}`;
          }
        }
      } catch (error) {
        console.error('Error parsing date:', error);
      }
      
      return null;
    };

    // Permission checks
    const canCreateCertificates = computed(() => hasPermission('Supplier.TenantCertificates.Create'));
    const canDeleteCertificates = computed(() => hasPermission('Supplier.TenantCertificates.Delete'));

    // Format file size
    const formatFileSize = (bytes) => {
      if (!bytes) return '0 B';
      
      const units = ['B', 'KB', 'MB', 'GB'];
      let size = bytes;
      let unitIndex = 0;
      
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }
      
      // Round to 2 decimal places if MB or GB, 0 decimal places if B or KB
      const decimals = unitIndex >= 2 ? 2 : 0;
      return `${size.toFixed(decimals)} ${units[unitIndex]}`;
    };

    // Update entity counts in store and emit event
    const updateCertificatesCount = async () => {
      try {
        // Get updated counts from API
        const response = await tenantInfoService.getEntityCounts();
        if (response) {
          // Update store with new counts 
          store.commit('updateEntityCounts', response);
          
          // Emit event to notify parent components
          emit('certificates-updated', {
            count: certificates.value.length,
            totalSize: totalSize.value
          });
        }
      } catch (error) {
        console.error('Error updating certificate counts:', error);
      }
    };

    const loadCertificates = async () => {
      if (!currentUser.value?.tenantId) {
        console.warn('No tenantId found in user data');
        return;
      }
      
      isLoading.value = true;
      error.value = null;
      
      try {
        const response = await tenantCertificateService.getTenantCertificates();
        
        if (response && Array.isArray(response)) {
          certificates.value = response.map(cert => ({
            id: cert.id,
            name: cert.certificateName,
            certificateTypeName: cert.certificateTypeName,
            certificateTypeId: cert.certificateTypeId,
            issueDate: cert.issueDate,
            expirationDate: cert.expirationDate,
            isExpired: cert.isExpired,
            daysUntilExpiration: cert.daysUntilExpiration,
            fileUploadId: cert.fileUploadId,
            fileUploadName: cert.fileUploadName,
            createdAt: cert.creationTime
          }));
        } else if (response && response.items) {
          // Handle paginated response
          certificates.value = response.items.map(cert => ({
            id: cert.id,
            name: cert.certificateName,
            certificateTypeName: cert.certificateTypeName,
            certificateTypeId: cert.certificateTypeId,
            issueDate: cert.issueDate,
            expirationDate: cert.expirationDate,
            isExpired: cert.isExpired,
            daysUntilExpiration: cert.daysUntilExpiration,
            fileUploadId: cert.fileUploadId,
            fileUploadName: cert.fileUploadName,
            createdAt: cert.creationTime
          }));
        } else {
          certificates.value = [];
        }
        
        // Calculate total size (not applicable for certificates, but kept for compatibility)
        totalSize.value = certificates.value.length;
        
        // Update the entity counts
        await updateCertificatesCount();
        
      } catch (error) {
        console.error('Error loading certificates:', error);
        error.value = 'Failed to load certificates';
        store.dispatch('notify', {
          message: 'Failed to load certificates',
          type: 'error'
        });
      } finally {
        isLoading.value = false;
      }
    };

    const downloadFile = async (cert) => {
      if (!cert || !cert.fileUploadId) {
        console.error('Invalid certificate object or missing fileUploadId');
        return;
      }
      
      try {
        isLoading.value = true;
        error.value = null;
        
        // Ensure file name has .pdf extension since we only allow PDF uploads
        const fileName = cert.fileUploadName || cert.name;
        const fileNameWithExtension = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
        
        const result = await fileManagementService.downloadFile(cert.fileUploadId, fileNameWithExtension);
        
        if (result && result.success) {
          store.dispatch('notify', {
            message: `Downloading ${result.filename || fileNameWithExtension}`,
            type: 'success'
          });
        }
      } catch (err) {
        console.error('Error downloading file:', err);
        error.value = 'Failed to download file';
        store.dispatch('notify', {
          message: 'Failed to download file',
          type: 'error'
        });
      } finally {
        isLoading.value = false;
      }
    };
    
    const handlePreviewFile = async (cert) => {
      if (!cert || !cert.fileUploadId) {
        console.error('Invalid certificate object or missing fileUploadId');
        return;
      }
      
      try {
        isLoadingPreview.value = true;
        previewingFile.value = cert;
        previewFile.value = null;
        previewError.value = null;
        
        // Show modal
        if (pdfPreviewModal) {
          pdfPreviewModal.show();
        }
        
        // Get file blob for preview
        const result = await fileManagementService.getFileBlob(cert.fileUploadId);
        
        if (result?.blob) {
          // Ensure file name has .pdf extension since we only allow PDF uploads
          const fileName = cert.fileUploadName || cert.name;
          const fileNameWithExtension = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
          
          // Create a File object from the blob for VueFilesPreview
          const file = new File([result.blob], fileNameWithExtension, {
            type: result.blob.type || 'application/pdf'
          });
          
          previewFile.value = file;
        } else {
          throw new Error('Failed to get file content');
        }
      } catch (err) {
        console.error('Error previewing file:', err);
        previewError.value = 'Failed to preview file: ' + (err.message || 'Unknown error');
        
        store.dispatch('notify', {
          message: 'Failed to preview file: ' + (err.message || 'Unknown error'),
          type: 'error'
        });
      } finally {
        isLoadingPreview.value = false;
      }
    };
    
    // Handle preview success
    const handlePreviewSuccess = () => {
      previewError.value = null;
    };
    
    // Handle preview error
    const handlePreviewError = (error) => {
      console.error('Error in preview:', error);
      previewError.value = 'Failed to load preview: ' + (error?.message || 'Unknown error');
      
      store.dispatch('notify', {
        message: 'Failed to load preview',
        type: 'error'
      });
    };
    
    // Download current preview
    const downloadCurrentPreview = () => {
      if (previewingFile.value) {
        downloadFile(previewingFile.value);
      }
    };

    // Load available certificates from databank
    const loadAvailableCertificates = async () => {
      try {
        const response = await certificateService.getCertificates();
        
        if (response && Array.isArray(response)) {
          availableCertificates.value = response;
        } else if (response && response.items) {
          availableCertificates.value = response.items;
        } else {
          availableCertificates.value = [];
        }
      } catch (error) {
        console.error('Error loading available certificates:', error);
        availableCertificates.value = [];
      }
    };

    // Open add certificate modal
    const openAddCertificateModal = () => {
      resetCertificateForm();
      loadAvailableCertificates();
      if (addCertificateModal) {
        addCertificateModal.show();
      }
    };

    // Reset certificate form
    const resetCertificateForm = () => {
      // Set today's date as default for issue date
      const today = new Date().toISOString().split('T')[0];
      
      certificateForm.value = {
        certificateId: '',
        issueDate: today,
        expireDate: '',
        certificateNumber: '',
        selectedFile: null,
        uploadedFileName: ''
      };
      certificateFormErrors.value = {
        certificateId: '',
        issueDate: '',
        expireDate: '',
        file: ''
      };
      selectedCertificateInterval.value = null;
      
      // Reset file input
      if (certificateFileInput.value) {
        certificateFileInput.value.value = '';
      }
      isCertificateFileUploading.value = false;
      certificateFileUploadProgress.value = 0;
    };

    // Handle certificate selection change
    const onCertificateChange = () => {
      const selectedCert = availableCertificates.value.find(
        cert => cert.id === certificateForm.value.certificateId
      );
      
      if (selectedCert && selectedCert.interval) {
        selectedCertificateInterval.value = selectedCert.interval;
        // If issue date is already set, recalculate expire date
        if (certificateForm.value.issueDate) {
          calculateExpireDate();
        }
      } else {
        selectedCertificateInterval.value = null;
        // If no interval, copy issue date to expire date when issue date is set
        if (certificateForm.value.issueDate) {
          certificateForm.value.expireDate = certificateForm.value.issueDate;
        }
      }
    };

    // Handle issue date change
    const onIssueDateChange = () => {
      if (!certificateForm.value.issueDate) return;
      
      if (selectedCertificateInterval.value) {
        calculateExpireDate();
      } else {
        // If no interval, copy issue date to expire date
        certificateForm.value.expireDate = certificateForm.value.issueDate;
      }
    };

    // Handle formatted date input changes
    const onFormattedIssueDateChange = (event) => {
      const value = event.target.value;
      formattedIssueDate.value = value;
      // Trigger change event for date calculation
      if (certificateForm.value.issueDate) {
        onIssueDateChange();
      }
    };

    const onFormattedExpireDateChange = (event) => {
      const value = event.target.value;
      formattedExpireDate.value = value;
    };

    // Calculate expire date based on interval (in years)
    const calculateExpireDate = () => {
      if (!certificateForm.value.issueDate || !selectedCertificateInterval.value) return;
      
      const issueDate = new Date(certificateForm.value.issueDate);
      const expireDate = new Date(issueDate);
      expireDate.setFullYear(expireDate.getFullYear() + selectedCertificateInterval.value);
      
      // Format date as YYYY-MM-DD for input[type="date"]
      certificateForm.value.expireDate = expireDate.toISOString().split('T')[0];
    };

    // Handle certificate file change (validation only, no upload)
    const onCertificateFileChange = (event) => {
      const file = event.target.files[0];
      if (!file) {
        certificateForm.value.selectedFile = null;
        certificateForm.value.uploadedFileName = '';
        return;
      }

      // Clear file validation error immediately when file is selected
      certificateFormErrors.value.file = '';

      // Validate file type
      if (file.type !== 'application/pdf') {
        certificateFormErrors.value.file = 'Only PDF files are allowed';
        certificateForm.value.selectedFile = null;
        certificateForm.value.uploadedFileName = '';
        return;
      }

      // Validate file size (18MB limit)
      const maxSize = 18 * 1024 * 1024;
      if (file.size > maxSize) {
        certificateFormErrors.value.file = `File size must be less than ${formatFileSize(maxSize)}`;
        certificateForm.value.selectedFile = null;
        certificateForm.value.uploadedFileName = '';
        return;
      }

      // Store the selected file for later upload after certificate creation
      certificateForm.value.selectedFile = file;
      certificateForm.value.uploadedFileName = file.name;
      
      // Clear validation error for valid files
      certificateFormErrors.value.file = '';
    };

    // Validate certificate form
    const validateCertificateForm = () => {
      let isValid = true;
      
      // Clear previous errors
      certificateFormErrors.value = {
        certificateId: '',
        issueDate: '',
        expireDate: '',
        file: ''
      };

      if (!certificateForm.value.certificateId) {
        certificateFormErrors.value.certificateId = 'Certificate type is required';
        isValid = false;
      }

      if (!certificateForm.value.issueDate) {
        certificateFormErrors.value.issueDate = 'Issue date is required';
        isValid = false;
      }

      if (!certificateForm.value.expireDate) {
        certificateFormErrors.value.expireDate = 'Expire date is required';
        isValid = false;
      }

      // Check if file has been selected
      if (!certificateForm.value.selectedFile) {
        certificateFormErrors.value.file = 'Certificate file is required';
        isValid = false;
      }

      // Check if expire date is after issue date
      if (certificateForm.value.issueDate && certificateForm.value.expireDate) {
        const issueDate = new Date(certificateForm.value.issueDate);
        const expireDate = new Date(certificateForm.value.expireDate);
        
        if (expireDate <= issueDate) {
          certificateFormErrors.value.expireDate = 'Expire date must be after issue date';
          isValid = false;
        }
      }

      return isValid;
    };

    // Cancel certificate modal
    const cancelCertificateModal = () => {
      // Close modal first
      if (addCertificateModal) {
        addCertificateModal.hide();
      }
      // Reset form will be called by the modal hidden event
    };

    // Helper: Get selected certificate info
    const getSelectedCertificate = () => {
      const selectedCert = availableCertificates.value.find(
        cert => cert.id === certificateForm.value.certificateId
      );
      
      if (!selectedCert) {
        throw new Error('Selected certificate not found');
      }
      
      return selectedCert;
    };

    // Helper: Prepare certificate data for API
    const prepareCertificateData = (selectedCert) => {
      return {
        certificateTypeId: certificateForm.value.certificateId,
        certificateName: certificateForm.value.certificateNumber || selectedCert.name,
        issueDate: certificateForm.value.issueDate,
        expirationDate: certificateForm.value.expireDate
      };
    };

    // Helper: Extract file upload ID from response
    const extractFileUploadId = (uploadResponse) => {
      if (!uploadResponse) return null;
      
      if (uploadResponse.success && uploadResponse.files?.length > 0) {
        return uploadResponse.files[0].id;
      }
      
      if (Array.isArray(uploadResponse) && uploadResponse.length > 0) {
        return uploadResponse[0].id;
      }
      
      if (uploadResponse.id) {
        return uploadResponse.id;
      }
      
      return null;
    };

    // Helper: Upload certificate file
    const uploadCertificateFile = async (certificateId) => {
      isCertificateFileUploading.value = true;
      certificateFileUploadProgress.value = 20;

      const fileDataArray = await fileManagementService.prepareFileUploadDtos(
        [certificateForm.value.selectedFile], 
        certificateId,
        "Certification"
      );

      certificateFileUploadProgress.value = 60;

      const uploadResponse = await fileManagementService.uploadFilesJsonAsync(
        certificateId,
        fileDataArray,
        "Certification"
      );

      certificateFileUploadProgress.value = 100;
      return uploadResponse;
    };

    // Helper: Update certificate with file upload ID
    const updateCertificateWithFile = async (certificateId, fileUploadId, selectedCert) => {
      const updateData = {
        id: certificateId,
        certificateTypeId: certificateForm.value.certificateId,
        certificateName: certificateForm.value.certificateNumber || selectedCert.name,
        issueDate: certificateForm.value.issueDate,
        expirationDate: certificateForm.value.expireDate,
        fileUploadId: fileUploadId
      };

      await tenantCertificateService.updateTenantCertificate(certificateId, updateData);
    };

    // Helper: Handle file upload success
    const handleFileUploadSuccess = async (certificateId, fileUploadId, selectedCert) => {
      await updateCertificateWithFile(certificateId, fileUploadId, selectedCert);
      store.dispatch('notify', {
        message: 'Certificate and file added successfully',
        type: 'success'
      });
    };

    // Helper: Handle file upload error
    const handleFileUploadError = (error) => {
      console.error('File upload error:', error);
      store.dispatch('notify', {
        message: `Certificate created but file upload failed: ${error.message}`,
        type: 'warning'
      });
    };

    // Helper: Finalize certificate creation
    const finalizeCertificateCreation = async () => {
      if (addCertificateModal) {
        addCertificateModal.hide();
      }
      resetCertificateForm();
      await loadCertificates();
    };

    // Submit certificate
    const submitCertificate = async () => {
      if (!validateCertificateForm()) return;

      isSubmittingCertificate.value = true;

      try {
        const selectedCert = getSelectedCertificate();
        const certificateData = prepareCertificateData(selectedCert);
        const response = await tenantCertificateService.createTenantCertificate(certificateData);

        if (!response?.id) {
          throw new Error('Failed to create certificate');
        }

        try {
          const uploadResponse = await uploadCertificateFile(response.id);
          const fileUploadId = extractFileUploadId(uploadResponse);

          if (fileUploadId) {
            await handleFileUploadSuccess(response.id, fileUploadId, selectedCert);
          } else {
            console.error('File upload failed - no valid file ID found in response:', uploadResponse);
            store.dispatch('notify', {
              message: 'Certificate created but file upload failed - no file ID returned',
              type: 'warning'
            });
          }
        } catch (fileUploadError) {
          handleFileUploadError(fileUploadError);
        }

        await finalizeCertificateCreation();
      } catch (error) {
        console.error('Error adding certificate:', error);
        store.dispatch('notify', {
          message: error.message || 'Failed to add certificate',
          type: 'error'
        });
      } finally {
        isSubmittingCertificate.value = false;
        isCertificateFileUploading.value = false;
        certificateFileUploadProgress.value = 0;
      }
    };
    
    const openFileUpload = () => {
      fileInput.value.click();
    };
    
    const handleFileUpload = async (event) => {
      const files = event.target.files;
      
      if (!files || files.length === 0) return;
      
      try {
        isUploading.value = true;
        uploadProgress.value = 10;
        error.value = null;
        
        // Check for PDF file type
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (file.type !== 'application/pdf') {
            throw new Error('Only PDF files are allowed');
          }
        }
        
        // Check for file size limits
        const maxSize = 18 * 1024 * 1024; // 18MB
        const totalUploadSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);
        
        if (totalUploadSize > maxSize) {
          throw new Error(`Total upload size exceeds the limit of ${formatFileSize(maxSize)}`);
        }
        
        // Check individual file sizes
        for (const file of files) {
          if (file.size > maxSize) {
            throw new Error(`File "${file.name}" exceeds the size limit of ${formatFileSize(maxSize)}`);
          }
        }
        
        uploadProgress.value = 30;
        
        // First create DTOs with base64 data
        const fileDataArray = await fileManagementService.prepareFileUploadDtos(
          Array.from(files), 
          currentUser.value.tenantId,
          "UserProfiles"
        );
        
        uploadProgress.value = 60;
        
        // Use JSON upload method with upload-json endpoint
        await fileManagementService.uploadFilesJsonAsync(
          currentUser.value.tenantId,
          fileDataArray,
          "UserProfiles"
        );
        
        uploadProgress.value = 100;
        
        // Show success notification
        store.dispatch('notify', {
          message: `Uploaded ${files.length} certificate(s) successfully`,
          type: 'success'
        });
        
        // Reload certificates
        await loadCertificates();
        
        // Update entity counts
        await updateCertificatesCount();
        
        // Reset file input
        if (fileInput.value) {
          fileInput.value.value = '';
        }
      } catch (err) {
        console.error('Error uploading files:', err);
        error.value = err.message || 'Failed to upload files';
        
        store.dispatch('notify', {
          message: error.value,
          type: 'error'
        });
      } finally {
        // Set a timeout to hide the progress bar
        setTimeout(() => {
          isUploading.value = false;
          uploadProgress.value = 0;
        }, 1000);
      }
    };

    const handleDelete = async (cert) => {
      if (!cert || !cert.id) {
        console.error('Invalid certificate object');
        return;
      }
      
      // Use SweetAlert for confirmation
      Swal.fire({
        title: 'Delete Certificate',
        text: `Are you sure you want to delete "${cert.name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            isLoading.value = true;
            deletingId.value = cert.id;
            
            await tenantCertificateService.deleteTenantCertificate(cert.id);
            
            store.dispatch('notify', {
              message: 'Certificate deleted successfully',
              type: 'success'
            });
            
            // Reload certificates
            await loadCertificates();
            
            // Update entity counts
            await updateCertificatesCount();
          } catch (error) {
            console.error('Error deleting certificate:', error);
            store.dispatch('notify', {
              message: 'Failed to delete certificate',
              type: 'error'
            });
          } finally {
            isLoading.value = false;
            deletingId.value = null;
          }
        }
      });
    };

    // Helper function to get correct file name for preview modal
    const getPreviewFileName = (cert) => {
      if (!cert || (!cert.fileUploadName && !cert.name)) return 'Document';
      
      const fileName = cert.fileUploadName || cert.name;
      return fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    };

    onMounted(() => {
      loadCertificates();
      
      // Initialize Bootstrap modals
      setTimeout(() => {
        if (window.bootstrap) {
          // PDF Preview Modal
          const modalElement = document.getElementById('pdfPreviewModal');
          if (modalElement) {
            pdfPreviewModal = new window.bootstrap.Modal(modalElement);
            
            // Reset preview when modal is hidden
            modalElement.addEventListener('hidden.bs.modal', () => {
              previewFile.value = null;
              previewingFile.value = {};
              previewError.value = null;
            });
          }

          // Add Certificate Modal
          const addCertModalElement = document.getElementById('addCertificateModal');
          if (addCertModalElement) {
            addCertificateModal = new window.bootstrap.Modal(addCertModalElement);
            
            // Reset form when modal is hidden
            addCertModalElement.addEventListener('hidden.bs.modal', () => {
              resetCertificateForm();
            });
          }
        }
      }, 100);
    });

    return {
      certificates,
      isLoading,
      isUploading,
      uploadProgress,
      error,
      fileInput,
      totalSize,
      formatFileSize,
      formatDate,
      downloadFile,
      openFileUpload,
      handleFileUpload,
      handleDelete,
      deletingId,
      handlePreviewFile,
      previewingFile,
      previewFileData: previewFile,
      previewError,
      isLoadingPreview,
      handlePreviewSuccess,
      handlePreviewError,
      downloadCurrentPreview,
      // Certificate modal functions
      openAddCertificateModal,
      availableCertificates,
      isSubmittingCertificate,
      selectedCertificateInterval,
      certificateForm,
      certificateFormErrors,
      onCertificateChange,
      onIssueDateChange,
      submitCertificate,
      cancelCertificateModal,
      // File upload functions
      certificateFileInput,
      isCertificateFileUploading,
      certificateFileUploadProgress,
      onCertificateFileChange,
      // Helper functions
      getPreviewFileName,
      // Date formatting
      dateFormat,
      formattedIssueDate,
      formattedExpireDate,
      onFormattedIssueDateChange,
      onFormattedExpireDateChange,
      parseFormattedDate,
      canCreateCertificates,
      canDeleteCertificates
    };
  }
};
</script>

<style scoped>
.progress {
  height: 0.5rem;
}

.avtar-s {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.certificate-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background-color: rgba(220, 53, 69, 0.1);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.certificate-icon:hover {
  background-color: rgba(220, 53, 69, 0.2);
  transform: scale(1.05);
}

.certificate-details {
  flex: 1;
  min-width: 0;
}

.certificate-details h6 {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.certificate-details a {
  font-size: 0.875rem;
}

.certificate-delete {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgba(220, 53, 69, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: all 0.2s ease;
}

.certificate-delete:hover {
  opacity: 1;
  background-color: rgba(220, 53, 69, 0.2);
}

.certificate-delete i {
  font-size: 14px;
}

.pdf-preview-frame {
  width: 100%;
  height: 75vh;
  border: none;
}

.preview-container {
  height: 70vh;
  overflow-y: auto;
}

/* Fix Excel preview scaling issue when scrolling */
:deep(.excel-preview) {
  height: auto !important;
  min-height: 70vh;
  overflow: visible !important;
  transform: none !important;
  transform-origin: unset !important;
}

:deep(.excel-preview table) {
  transform: none !important;
  min-width: 100%;
}

:deep(.excel-preview .x-spreadsheet) {
  height: 70vh !important;
  overflow: visible;
  transform: none !important;
}

:deep(.excel-preview .x-spreadsheet-sheet) {
  transform: none !important;
  height: auto !important;
}
</style> 